/**
 * UpsellDialog — generic Plan-limit upsell prompt.
 *
 * Triggered when a creation path (POST /workers, POST /branches, …)
 * comes back with HTTP 402 from the platform's enforce_*_limit
 * helpers (see app/core/licensing.py). The backend already includes
 * a human-readable detail string carrying the resource type and the
 * current/max numbers; we surface it verbatim with a clear path to
 * /system/billing so the admin can upgrade in one click.
 *
 * Phase C of PRODUCT_SUBSCRIPTION_STRATEGY.md §6: the third leg of
 * the upsell funnel. UsageBanner (Phase B) handles the soft "you
 * are approaching" nudge at >= 80%; this dialog handles the hard
 * "you cannot create another one" stop at == 100%.
 */
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import WorkspacePremiumOutlined from '@mui/icons-material/WorkspacePremiumOutlined';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../../api/client';

interface Props {
  open: boolean;
  /** Human-readable detail from the 402 response. Already includes
   *  the current/max counts and the resource label, so we render it
   *  verbatim. Empty string = generic fallback copy. */
  message: string;
  /** Optional override for the upgrade target path (e.g. for
   *  workers in a sub-org). Defaults to /system/billing. */
  upgradePath?: string;
  onClose: () => void;
}

const UpsellDialog: React.FC<Props> = ({
  open,
  message,
  upgradePath = '/system/billing',
  onClose,
}) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate(upgradePath);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: 3, minWidth: 380, maxWidth: 460 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <WorkspacePremiumOutlined sx={{ color: 'warning.main' }} />
        Plan limit reached
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning" sx={{ alignItems: 'flex-start' }}>
            <Typography variant="body2">
              {message ||
                'You have reached the limit allowed by your current plan.'}
            </Typography>
          </Alert>
          <Box>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mb: 0.5 }}
            >
              You can either:
            </Typography>
            <Stack component="ul" sx={{ pl: 2.5, m: 0 }} spacing={0.5}>
              <Typography component="li" variant="body2">
                Upgrade your plan to lift the limit and continue right away.
              </Typography>
              <Typography component="li" variant="body2">
                Free up capacity by deactivating workers / removing branches
                you no longer use, then try again.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Maybe later
        </Button>
        <Button
          onClick={handleUpgrade}
          variant="contained"
          color="primary"
        >
          View plans
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpsellDialog;


// ---------------------------------------------------------------------------
// useUpsellOnLimit — small helper hook for the common 402 catch pattern.
// ---------------------------------------------------------------------------
//
// Usage:
//   const { upsell, openIfLimit, closeUpsell } = useUpsellOnLimit();
//   try { await api.create(...) }
//   catch (e) {
//     if (openIfLimit(e)) return;  // upsell shown, swallow the error
//     // … other handling
//   }
//   <UpsellDialog open={upsell.open} message={upsell.message} onClose={closeUpsell} />
//
// Centralising the (status === 402) check + state plumbing here keeps
// every creation path one line away from a properly wired upsell.

export const useUpsellOnLimit = () => {
  const [upsell, setUpsell] = React.useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: '' });

  /** Returns true when the error was a 402 limit and the dialog has
   *  been opened — caller should treat the error as handled. Returns
   *  false for any other error so callers can fall through to their
   *  existing handling. */
  const openIfLimit = (e: unknown): boolean => {
    if (e instanceof ApiError && e.status === 402) {
      setUpsell({ open: true, message: e.message });
      return true;
    }
    return false;
  };

  const closeUpsell = () => setUpsell({ open: false, message: '' });

  return { upsell, openIfLimit, closeUpsell };
};
